import React from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import styles from "./Hero.module.css";
import { useLang } from "../context/LanguageContext";

const wordV = {
  hidden:  { opacity: 0, y: 28, filter: "blur(6px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
};

const Hero = () => {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const opacity   = useTransform(scrollY, [0, 340], [1, 0]);
  const yParallax = useTransform(scrollY, [0, 500], [0, -40]);

  return (
    <section className={styles.hero}>
      {/* Background photo */}
      <img src="/assets/hero.png" alt="" className={styles.bgPhoto} aria-hidden="true" />
      <div className={styles.bgOverlay} />

      {/* Decorative layers */}
      <div className={styles.gridLines} />
      <div className={styles.diag1} />
      <div className={styles.diag2} />
      <div className={styles.diag3} />
      <div className={styles.glowCenter} />

      <motion.div
        className={styles.scanLine}
        initial={{ y: 0 }}
        animate={{ y: "101vh" }}
        transition={{ duration: 0.5, ease: "easeIn" }}
      />

      {/* Content — centered */}
      <motion.div className={styles.inner} style={{ opacity, y: yParallax }}>

        <motion.h1
          className={styles.title}
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.065, delayChildren: 0.45 } } }}
          aria-label={t.hero.ariaLabel}
        >
          {t.hero.words.map((w, i) => (
            <motion.span
              key={i}
              className={w.a ? `${styles.word} ${styles.accentWord}` : styles.word}
              variants={wordV}
            >
              {w.t}{i < t.hero.words.length - 1 ? "\u00a0" : ""}
            </motion.span>
          ))}
        </motion.h1>

        <motion.p
          className={styles.sub}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.5 }}
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          className={styles.ctas}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.45 }}
        >
          <Link to="/contact" className={styles.ctaPrimary}>
            {t.hero.ctaPrimary}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
          <Link to="/services" className={styles.ctaGhost}>{t.hero.ctaGhost}</Link>
        </motion.div>

        <motion.div
          className={styles.statsRow}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.5 }}
        >
          {t.hero.stats.map(([v, l]) => (
            <div key={l} className={styles.stat}>
              <span className={styles.statVal}>{v}</span>
              <span className={styles.statLabel}>{l}</span>
            </div>
          ))}
        </motion.div>

      </motion.div>

      <motion.div
        className={styles.scrollCue}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.9, duration: 0.6 }}
      >
        <span className={styles.scrollLine} />
        <span className={styles.scrollText}>SCROLL</span>
      </motion.div>
    </section>
  );
};

export default Hero;
